import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createSuperAdmin } from '../../api/admin';
import Input from '../ui/Input';
import { useToast } from '../../context/ToastContext';
import { getFriendlyError } from '../../utils/friendlyError';

const emptyForm = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
};

const AddSuperAdminModal = ({ open, onClose, onCreated }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [form, setForm] = useState(emptyForm);

  const createMut = useMutation({
    mutationFn: () =>
      createSuperAdmin({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        password: form.password,
      }),
    onSuccess: (res) => {
      showToast(res.message || t('admin.users.superAdminCreated'), 'success');
      setForm(emptyForm);
      onCreated?.();
      onClose();
    },
    onError: (err) => showToast(getFriendlyError(err, t('admin.users.superAdminCreateFailed')), 'error'),
  });

  if (!open) return null;

  const canSubmit =
    form.firstName.trim() &&
    form.lastName.trim() &&
    form.email.trim().includes('@') &&
    form.password.length >= 8 &&
    form.password === form.confirmPassword;

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50"
        onClick={() => {
          if (!createMut.isPending) onClose();
        }}
      />
      <div className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-zinc-200 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900">
        <h2 className="text-lg font-semibold">{t('admin.users.addSuperAdminTitle')}</h2>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
          {t('admin.users.addSuperAdminSubtitle')}
        </p>

        <form
          className="mt-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit && !createMut.isPending) createMut.mutate();
          }}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label={`${t('auth.firstName')} *`}
              value={form.firstName}
              onChange={setField('firstName')}
              autoComplete="given-name"
              required
            />
            <Input
              label={`${t('auth.lastName')} *`}
              value={form.lastName}
              onChange={setField('lastName')}
              autoComplete="family-name"
              required
            />
          </div>
          <Input
            label={`${t('auth.email')} *`}
            type="email"
            value={form.email}
            onChange={setField('email')}
            autoComplete="email"
            required
          />
          <Input
            label={`${t('admin.users.temporaryPassword')} *`}
            type="password"
            value={form.password}
            onChange={setField('password')}
            minLength={8}
            autoComplete="new-password"
            required
          />
          <Input
            label={`${t('profile.confirmNewPassword')} *`}
            type="password"
            value={form.confirmPassword}
            onChange={setField('confirmPassword')}
            autoComplete="new-password"
            required
          />
          <p className="text-xs text-zinc-500">{t('admin.users.mustChangePasswordHint')}</p>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={createMut.isPending}
              className="flex-1 rounded-lg border border-zinc-300 py-2 text-sm dark:border-zinc-700"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={!canSubmit || createMut.isPending}
              className="flex-1 rounded-lg bg-glow-600 py-2 text-sm text-white disabled:opacity-50"
            >
              {createMut.isPending ? t('common.saving') : t('admin.users.addSuperAdmin')}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddSuperAdminModal;
